import { Box, RateSelection as DSRateSelection, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { forwardRef, memo, type Ref } from 'react';
import { UserProfession } from '~/enums/UserProfession';
import { useLikelyToFillExperiment } from '../hooks/useLikelyToFillExperiment';
import { useShiftRateRecommendation } from '../hooks/useShiftRateRecommendation';
import { getLikelyToFillBand } from './likelyToFillBand';
import { getRateLikelihood } from './rateLikelihood';

interface IRateSelectionProps {
  value?: number | null;
  onChange?: (value: number) => void;
  professionType: string;
  officeId: string;
  hasError?: boolean;
  /** Selected shift dates — used to detect a single last-minute shift (enhanced rate). */
  dates?: Date[];
  /** Shift arrival time — combined with the date for the 48h enhanced-rate window. */
  arrivalTime?: string;
  /**
   * Receives the recommendation at the moment it prefills the rate so the request can store it.
   * Pass it only from the shift-request Shift Details step — see `useShiftRateRecommendation`.
   */
  onRecommendedRateChange?: (recommendedRate: number) => void;
}

const RATE_STEP = 1;

/**
 * Professional-pay rate stepper for the new shift-request form. Owns the domain
 * logic — the office's recommended rate, the rate floor/ceiling clamping and the
 * rate→likelihood mapping (or, in the GOTU-13065 treatment arm, the dollar-gap
 * Likely to Fill band) — and delegates all presentation (the −/+ stepper, the
 * `$value`, the indicator, its alert and the helper line) to the DS `RateSelection`.
 */
const Component = (
  {
    value,
    onChange,
    professionType,
    officeId,
    dates,
    arrivalTime,
    onRecommendedRateChange,
  }: IRateSelectionProps,
  ref: Ref<HTMLDivElement>,
) => {
  const { profRate, averageRate, recommendedRate, effectiveMin, hasRecommendation } =
    useShiftRateRecommendation({
      professionType,
      officeId,
      value,
      onChange,
      dates,
      arrivalTime,
      onRecommendedRateChange,
    });

  const hasValue = value != null;
  // Show no rate (`$ --`) until the office's own rate loads, never the $49 placeholder.
  const fallbackDisplayValue = hasRecommendation ? recommendedRate : null;
  const displayValue = hasValue ? value : fallbackDisplayValue;
  const canDecrement = Boolean(officeId) && hasValue && value > effectiveMin;
  const canIncrement = Boolean(officeId) && hasValue && value < profRate.max;

  const handleDecrement = () => {
    onChange?.(Math.max(effectiveMin, (value ?? effectiveMin) - RATE_STEP));
  };

  const handleIncrement = () => {
    onChange?.(Math.min(profRate.max, (value ?? recommendedRate) + RATE_STEP));
  };

  const likelihood =
    displayValue != null && hasRecommendation ? getRateLikelihood(displayValue, averageRate) : null;

  // The DS shows `indicator` over `likelihood`; null (control, or a profession outside the test)
  // falls back to today's likelihood line.
  const { isTreatment, bands } = useLikelyToFillExperiment();
  const indicator =
    isTreatment && displayValue != null && hasRecommendation
      ? getLikelyToFillBand(
          displayValue,
          averageRate,
          UserProfession.getCode(professionType) as string | undefined,
          bands,
        )
      : null;

  return (
    <Box pt={8}>
      {/* Section header — same format as the "Hours" section. */}
      <Typography as="span" bold size="heading_3" color={colors.neutral[600]}>
        Professional Pay
      </Typography>
      <Box style={{ height: '1.5px', backgroundColor: colors.neutral[100], marginTop: 12 }} />

      <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
        <DSRateSelection
          ref={ref}
          data-testid="rate-selection"
          data-cy="rate-selection"
          value={displayValue}
          likelihood={likelihood}
          indicator={indicator}
          onDecrement={handleDecrement}
          onIncrement={handleIncrement}
          decrementDisabled={!canDecrement}
          incrementDisabled={!canIncrement}
        />
      </Box>
    </Box>
  );
};

export const RateSelection = memo(forwardRef(Component));
